import React, { useState, useEffect } from 'react';
import { Button, Box, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import APIUtils from '../../utils/APIUtils.js';

// Usage examples:
{/* 
//Default
<HGVSNames variantId={variantId} />
// Show 3 items initially
<HGVSNames variantId={variantId} itemsToShow={3} />
// Show 5 items initially with bold text
<HGVSNames variantId={variantId} itemsToShow={5} bold={true} /> 
*/}

const HGVSNames = ({ variantId, displayAs = 'list', bold = false, itemsToShow = 1, onLoaded }) => {
  const [hgvsNames, setHgvsNames] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (variantId) {
      getHgvsNames(variantId);
    } else {
      setLoading(false);
      onLoaded?.();
    }
  }, [variantId]); // This will run whenever variantId changes

  const getHgvsNames = async (variantId) => {
    setLoading(true);
    try {
      const response = await APIUtils.getHgvsNames(variantId);
      if (response) {
        setHgvsNames(response);
      }
    } catch (error) {
      console.error('Failed to load HGVS names.');
    } finally {
      setLoading(false);
      onLoaded?.();
    }
  };

  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const sortedHgvsNames = hgvsNames.sort();
  const displayedItems = showAll ? sortedHgvsNames : sortedHgvsNames.slice(0, itemsToShow);
  const hasMoreItems = sortedHgvsNames.length > itemsToShow;

  const renderAsString = () => (
    <Box component="span" sx={{ fontWeight: bold ? 'bold' : 'normal' }}>
      {displayedItems.join(", ")}
      {hasMoreItems && !showAll && (
        <>
          {", "}
          <Button
            size="small"
            onClick={toggleShowAll}
            startIcon={<ExpandMore />}
            sx={{ 
              minWidth: 'auto',
              padding: '2px 8px',
              fontSize: '0.75rem',
              textTransform: 'none',
              marginLeft: '4px'
            }}
          >
            +{sortedHgvsNames.length - itemsToShow} more
          </Button>
        </>
      )}
      {hasMoreItems && showAll && (
        <Button
          size="small"
          onClick={toggleShowAll}
          startIcon={<ExpandLess />}
          sx={{ 
            minWidth: 'auto',
            padding: '2px 8px',
            fontSize: '0.75rem',
            textTransform: 'none',
            marginLeft: '8px'
          }}
        >
          Show less
        </Button>
      )}
    </Box>
  );

  const renderAsList = () => (
    <Box>
      <List dense sx={{ padding: 0 }}>
        {displayedItems.map((item, index) => (
          <ListItem key={index} sx={{ padding: '2px 0' }}>
            <ListItemText 
              primary={item} 
              sx={{ 
                margin: 0, 
                '& .MuiListItemText-primary': { 
                  fontSize: '0.875rem',
                  fontWeight: bold ? 'bold' : 'normal'
                } 
              }}
            />
          </ListItem>
        ))}
      </List>
      {hasMoreItems && (
        <Box sx={{ marginTop: 1 }}>
          {!showAll ? (
            <Button
              size="small"
              onClick={toggleShowAll}
              startIcon={<ExpandMore />}
              sx={{ 
                minWidth: 'auto',
                padding: '2px 8px',
                fontSize: '0.75rem',
                textTransform: 'none'
              }}
            >
              +{sortedHgvsNames.length - itemsToShow} more
            </Button>
          ) : (
            <Button
              size="small"
              onClick={toggleShowAll}
              startIcon={<ExpandLess />}
              sx={{ 
                minWidth: 'auto',
                padding: '2px 8px',
                fontSize: '0.75rem',
                textTransform: 'none'
              }}
            >
              Show less
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      {loading ? (
        <CircularProgress aria-label="Loading HGVS names" color="inherit" size={16} />
      ) : hgvsNames.length > 0 ? (
        displayAs === 'list' ? renderAsList() : renderAsString()
      ) : (
        ''
      )}
    </Box>
  );
};

export default HGVSNames;